// Per-page <head> management for the /guides pages. The site is a SPA, so crawlers see these
// once the page has rendered (Google renders JavaScript); the sitemap is served by the API.
import { APP_BASE_URL } from '@/config/branding'

const MARKER = 'data-guides-meta'
let originalTitle = null
let originalDescription = null

export const SITE_ORIGIN = process.env.NODE_ENV === 'production' ? APP_BASE_URL : window.location.origin

export function absoluteUrl(path) {
  return `${SITE_ORIGIN}${path}`
}

function upsert(selector, create) {
  let element = document.head.querySelector(selector)
  if (!element) {
    element = create()
    element.setAttribute(MARKER, '')
    document.head.appendChild(element)
  }
  return element
}

function setMetaTag(attribute, key, content) {
  const element = upsert(`meta[${attribute}="${key}"]`, () => {
    const meta = document.createElement('meta')
    meta.setAttribute(attribute, key)
    return meta
  })
  element.setAttribute('content', content)
}

/**
 * @param {{ title: string, description?: string, path: string, robots?: string, image?: string, type?: string, jsonLd?: object }} meta
 */
export function setPageMeta({ title, description, path, robots = 'index, follow', image, type = 'website', jsonLd }) {
  if (originalTitle === null) {
    originalTitle = document.title
    originalDescription = document.head.querySelector('meta[name="description"]')?.getAttribute('content') || ''
  }
  clearPageMeta(false)
  const url = absoluteUrl(path)
  document.title = title
  setMetaTag('name', 'description', description || originalDescription)
  setMetaTag('name', 'robots', robots)
  setMetaTag('property', 'og:title', title)
  setMetaTag('property', 'og:description', description || originalDescription)
  setMetaTag('property', 'og:type', type)
  setMetaTag('property', 'og:url', url)
  if (image) setMetaTag('property', 'og:image', image)
  setMetaTag('name', 'twitter:card', image ? 'summary_large_image' : 'summary')

  const canonical = upsert('link[rel="canonical"]', () => {
    const link = document.createElement('link')
    link.setAttribute('rel', 'canonical')
    return link
  })
  canonical.setAttribute('href', url)

  if (jsonLd) {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute(MARKER, '')
    script.textContent = JSON.stringify(jsonLd)
    document.head.appendChild(script)
  }
}

/** Removes everything setPageMeta added and restores the site-wide title/description. */
export function clearPageMeta(restore = true) {
  document.head.querySelectorAll(`[${MARKER}]`).forEach(element => element.remove())
  if (restore && originalTitle !== null) {
    document.title = originalTitle
    document.head.querySelector('meta[name="description"]')?.setAttribute('content', originalDescription)
  }
}
